import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography
} from "@mui/material";
import { useFormik } from "formik";
import { PhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useConfig } from "../../utils/useConfig";
import { TagReference, useData } from "../../utils/useData";
import { PlanSelection } from "../modals/shared/PlanSelection";
import { TagSelection } from "../modals/shared/TagSelection";
import { PersonalDetailsForm } from "./PersonalDetailsForm";

type AddUserProps = {
  open: boolean;
  handleClose: () => void;
};

type FormValuesType = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const DEFAULT_VALUE = "-1";

export const AddUser = ({ open, handleClose }: AddUserProps) => {
  const { config } = useConfig();
  const { employerId, employees, employer } = useData();
  const { getToken } = useKindeAuth();

  const [sendInvitationEmail, setSendInvitationEmail] = useState(true);
  const [tags, setTags] = useState<TagReference[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<
    { planId: string; tierId: string }[]
  >([]);

  const [error, setError] = useState("");
  const initialValues: FormValuesType = {
    email: "",
    phone: "",
    firstName: "",
    lastName: ""
  };

  const addEmployeeMutation = useMutation(
    async (formValues: FormValuesType) => {
      if (!selectedPlans.length)
        throw new Error("Must select at least 1 plan for user");

      const countryCode: any = employer?.data?.country?.id;
      const formattedPhoneNumber = parsePhoneNumberFromString(
        formValues.phone,
        countryCode
      )?.number;

      const token = await getToken();
      const response = await fetch(
        `${config?.API_URL}/employers/${employerId}/employees`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            sendInvitationEmail,
            contact: {
              email: formValues.email,
              phone: formattedPhoneNumber,
              address: {
                country: employer.data?.country.id ?? ""
              }
            },
            plans: selectedPlans.filter(
              (plan) => plan.tierId !== DEFAULT_VALUE
            ),
            tags
          })
        }
      );

      if (!response.ok) {
        switch (response.status) {
          case 409: {
            throw new Error(
              "This email already exists or the account has been deleted. Please contact support"
            );
          }
          default: {
            throw new Error("There was a problem adding this user");
          }
        }
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("User added successfully");
        employees.refetch();
        handleClose();
      },
      onError: (error: Error) => {
        console.error(error.message);
        setError(error.message);
      }
    }
  );

  const isMobileNumber = (phoneNumber: PhoneNumber, countryCode: string) => {
    switch (countryCode) {
      case "NZ":
        return phoneNumber.nationalNumber.startsWith("2");
      case "AU":
        return phoneNumber.nationalNumber.startsWith("4");
      default:
        return false;
    }
  };

  const useDynamicValidationSchema = (countryCode: any) => {
    return useMemo(() => {
      const phoneNumberValidation = yup
        .string()
        .test(
          "is-valid-mobile-phone",
          `Invalid ${countryCode} mobile phone number`,
          function (value) {
            if (!value) return true; // Allow empty values
            try {
              const phoneNumber: PhoneNumber | undefined =
                parsePhoneNumberFromString(value, countryCode);
              if (!phoneNumber) {
                return false;
              }
              //const numberType = getNumberType(phoneNumber.nationalNumber, countryCode); -> doesn't work for some reason, tried with custom metadata (check project.json scripts)
              return (
                phoneNumber.isValid() &&
                isMobileNumber(phoneNumber, countryCode)
              );
            } catch (error) {
              return this.createError({
                message: `Invalid phone number for country: ${countryCode}`
              });
            }
          }
        );

      return yup.object().shape({
        firstName: yup.string().max(100).required(),
        lastName: yup.string().max(100).required(),
        phone: phoneNumberValidation,
        email: yup.string().email("Invalid email").required()
      });
    }, [countryCode]);
  };

  const validationSchema = useDynamicValidationSchema(
    employer.data?.country.id ?? ""
  );

  const { resetForm, ...formik } = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      addEmployeeMutation.mutate(values);
    },
    enableReinitialize: true
  });

  // Reset form when the modal is opened
  useEffect(() => {
    if (open) {
      resetForm();
      setSelectedPlans([]);
      setTags([]);
      setError("");
    }
  }, [open, resetForm]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth={true}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Add new user</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            <Grid container spacing={2} item xs={12}>
              <PersonalDetailsForm form={formik} allowEmailChange={true} />
            </Grid>
            <Grid container item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked
                    value={sendInvitationEmail}
                    onChange={(e) => setSendInvitationEmail(e.target.checked)}
                  />
                }
                label="Send invitation email"
              />
            </Grid>
            <Grid container spacing={1} item xs={12}>
              <PlanSelection
                selectedPlans={selectedPlans}
                setSelectedPlans={setSelectedPlans}
                DEFAULT_VALUE={DEFAULT_VALUE}
              />
            </Grid>
            <Grid container spacing={1} item xs={12}>
              <TagSelection tags={tags} setTags={setTags} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={addEmployeeMutation.isLoading || employer.isLoading}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
